// Helper class to parse markdown text into a hierarchical structure
// of sections and subsections for the Quick Help section.
export class QuickHelpParser {
  constructor() {
    this.markedInstance = null;
    this.fileMap = null;
  }

  async initialize() {
    // Import marked and file map async
    const marked = (await import('marked')).marked;
    const markedKatex = (await import('marked-katex-extension')).default;
    const { FileMap } = await import('../QuickHelp/MarkdownFiles.js');
    
    // Setup marked with katex
    const options = {
      output: 'mathml',
      throwOnError: true,
    };
    marked.use(markedKatex(options));
    
    this.markedInstance = marked;
    this.fileMap = FileMap;
  }

  async parseFile(fileId) {
    // Ensure we're initialized
    if (!this.markedInstance || !this.fileMap) {
      await this.initialize();
    }

    if (!(fileId in this.fileMap)) {
      return null;
    }

    const markdownText = this.fileMap[fileId];
    return this.parse(markdownText);
  }

  parse(markdownText) {
    // Parse the markdown text to a hierarchical structure of sections and subsections.
    // Each section has 'title', 'level', 'content' (which is a list of child sections),
    // and 'text'.

    let root = { title: 'Root', level: 0, content: [], text: null };
    let stack = [root];
    
    const lines = markdownText.split('\n');
    for (let line of lines) {
      const headerMatch = line.match(/^(#+)\s+(.*)$/);
      if (headerMatch) {
        const level = headerMatch[1].length;
        const title = headerMatch[2].trim();
        const newSection = { title, level, content: [], text: null };

        // Pop from stack until we find the correct parent level
        while (stack.length > 0 && stack[stack.length - 1].level >= level) {
          stack.pop();
        }
        
        // Add the new section to the current top of the stack
        stack[stack.length - 1].content.push(newSection);
        stack.push(newSection);
      } else {
        // If it's not a header, add it as content to the current section
        if (stack.length > 0) {
          if (!stack[stack.length - 1].text) {
            stack[stack.length - 1].text = '';
          }
          stack[stack.length - 1].text += line + '\n';
        }
      }
    }

    // Fixup h1 nodes: For all h1 nodes, make their 'text' a h2 section called 'Overview'.
    // (Unless they have no h2 children, in which case keep as is).
    // This makes the files more skimmable.
    function fixupH1Nodes(node) {
      if (node.level === 1 & node.text !== null && node.content.length > 0) {
        const overviewSection = { title: 'Overview', level: 2, content: [], text: node.text };
        node.text = null;
        node.content.unshift(overviewSection);
      }
      for (const child of node.content) {
        fixupH1Nodes(child);
      }
    }
    fixupH1Nodes(root);

    // Generate HTML for each section if marked instance is available
    if (this.markedInstance) {
      this._generateHtml(root);
    }

    return root.content;
  }

  /**
   * Generate HTML for all sections in the tree
   * @param {Object} node - Node to process
   * @private
   */
  _generateHtml(node) {
    if (node.text && this.markedInstance) {
      const renderer = new this.markedInstance.Renderer();
      renderer.image = (image) => {
        const modifiedHref = `/QuickHelpAssets/${image.href}`;
        return `<img src="${modifiedHref}" alt="${image.text}" ${image.title ? `title="${image.title}"` : ""} />`;
      };
      node.html = this.markedInstance(node.text, { renderer });
    }

    // Process children
    for (const child of node.content) {
      this._generateHtml(child);
    }
  }
}
